import { useGraphQLQuery } from '../../hooks/useGraphQLRequest';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { type ResultOf } from '@graphql-typed-document-node/core';
import { graphql } from '@mp/graphql/generated';
import { IAssignment } from '@medely/types';

const FAVORITABLE_ASSIGNMENT_STATUSES = ['available', 'matched'];
const UNAPPLIED_STATUSES = ['unapplied'];
const ALL_APPLICATION_STATUSES = ['unapplied', 'applied', 'offer_sent'];

const FavoriteAssignmentsGql = graphql(`
  query GetFavoritedAssignmentsForFavoriteButton($input: ProfessionalAssignmentFavoriteQueryInput) {
    professionalAssignmentFavorites(input: $input) {
      id
      assignment_id
      assignment {
        id
        is_w2
        is_travel
        starts_date
        ends_date
        status
        call_requirements
        positions {
          display_name
        }
        primary_location {
          id
          name
          address_first
          address_second
          address_city
          address_zip
          state {
            abbreviation
          }
          timezone_lookup
        }
        pay_per_week_cents: pay_per_week_cents_for_account_id
        hours_per_shift
        number_of_shifts_per_week
        starts_time
        ends_time
        specialties {
          id
          label
        }
        one_time_bonus_burden_cents
        first_to_apply
        distance_in_miles
        less_than_300_miles_away
        great_pay
        missing_condition_ids
      }
    }
  }
`);

const FavoriteAssignmentsAgencyIrpUserGql = graphql(`
  query GetFavoritedAssignmentsIRPForFavoriteButton(
    $input: ProfessionalAssignmentFavoriteQueryInput
  ) {
    professionalAssignmentFavorites(input: $input) {
      id
      assignment_id
      assignment {
        id
        is_w2
        is_travel
        starts_date
        ends_date
        status
        call_requirements
        positions {
          display_name
        }
        primary_location {
          id
          name
          address_first
          address_second
          address_city
          address_zip
          state {
            abbreviation
          }
          timezone_lookup
        }
        hours_per_shift
        number_of_shifts_per_week
        starts_time
        ends_time
        specialties {
          id
          label
        }
        missing_condition_ids
      }
    }
  }
`);

export const useFavoriteAssignmentsQuery = (params: { onlyUnapplied?: boolean } = {}) => {
  const { isAgency, isSaas } = useCurrentUser();
  const { onlyUnapplied = false } = params;

  const FavoritesGql =
    isAgency || isSaas ? FavoriteAssignmentsAgencyIrpUserGql : FavoriteAssignmentsGql;
  const OperationName =
    isAgency || isSaas
      ? 'GetFavoritedAssignmentsIRPForFavoriteButton'
      : 'GetFavoritedAssignmentsForFavoriteButton';
  const { data, isLoading, isFetching, refetch } = useGraphQLQuery<ResultOf<typeof FavoritesGql>>({
    operationName: OperationName,
    queryKey: ['favoriteAssignments', onlyUnapplied],
    query: FavoritesGql,
    variables: {
      input: {
        search: {
          assignment_statuses: FAVORITABLE_ASSIGNMENT_STATUSES,
          last_assignment_request_statuses: onlyUnapplied
            ? UNAPPLIED_STATUSES
            : ALL_APPLICATION_STATUSES,
        },
      },
    },
  });

  const result = data?.professionalAssignmentFavorites || [];

  const ids = result.map((item) => item.assignment_id);
  const assignments = result.map((item) => item.assignment as IAssignment);

  const isFavorited = (assignmentId: number): boolean => {
    return ids.includes(assignmentId);
  };

  return {
    ids,
    assignments,
    count: result.length,
    loading: isLoading,
    isFetching: !!isFetching,
    isFavorited,
    refetch,
  };
};
